import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { formatISO, parseISO } from 'date-fns';
import * as yup from 'yup';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import ExpandedSection from '@components/ExpandedSection';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { accountActions, alertActions } from '@actions';
import { companyService } from '@services';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { ChatType, paymentMethods, PaymentTypes } from '@constants/companies';
import { LANGUAGE } from '@constants/language';
import { handleApiError } from '@helpers/api';
import { isObject } from '@helpers/isObject';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';
import IntegrationsTable from '../IntegrationsTable';
import Profiles from '../Profiles';
import GeneralInfo from './GeneralInfo';
import Contacts from './Contacts';
import Address from './Address';
import AdditionalInfo from './AdditionalInfo';
import BusinessHours from './BusinessHours';
import Appearance from './Appearance';
import PaymentMethods from './PaymentMethods';
import CategoriesAndServices from './CategoriesAndServices';
import { Container, Content, Footer } from './styles';

const EditForm = ({ filter, page, rowsPerPage, view, setView, selectedCompany, setSelectedCompany, account }) => {
    const [section, setSection] = useState({
        general: true,
        contact: true,
        address: true,
        additionalInfo: true,
        businessHours: true,
        appearance: true,
        paymentMethods: true,
        services: true,
    });
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const { data: countries, loading: countriesLoading } = useSelector(state => state.countries);

    const categoriesDisplayProp = {
        [LANGUAGE.ENGLISH]: 'displayNameEn',
        [LANGUAGE.SPANISH]: 'displayNameEs',
        [LANGUAGE.PORTUGUESE]: 'displayNamePt',
        [LANGUAGE.UKRAINIAN]: 'displayNameUk',
        [LANGUAGE.PORTUGUESE_BRAZIL]: 'displayNameBr',
    };
    const dispatch = useDispatch();

    const CompanyFormSchema = yup.object().shape(
        {
            locationName: yup.string().required('validation.required'),
            internalName: yup.string().required('validation.required'),
            legalName: yup.string(),
            status: yup.number().required('validation.required'),
            phoneNumber: yup.string().validatePhone('validation.wrongPhoneFormat').required('validation.required'),
            phoneExtension: yup.string().when('phoneExtension', {
                is: val => !val?.length,
                then: yup.string().notRequired(),
                otherwise: yup.string().matches(/^\d+$/),
            }),
            email: yup.string().nullable().email('validation.wrongEmailFormat'),
            website: yup.string().url('validation.invalidURL'),
            chatLink: yup.string().when('chatLinkAdded', {
                is: true,
                then: yup.string().validatePhone('validation.wrongPhoneFormat'),
                otherwise: yup.string().notRequired(),
            }),
            locationWebsites: yup.array().of(
                yup.object().shape({
                    url: yup.string().required('validation.required').url('validation.invalidURL'),
                }),
            ),
            hasBusinessAddress: yup.boolean(),
            addressLine1: yup.string().when('hasBusinessAddress', {
                is: true,
                then: yup.string().required('validation.required'),
                otherwise: yup.string().nullable(),
            }),
            city: yup.string().when('hasBusinessAddress', {
                is: true,
                then: yup.string().required('validation.required'),
                otherwise: yup.string().nullable(),
            }),
            country: yup.string().when('hasBusinessAddress', {
                is: true,
                then: yup.string().required('validation.required'),
                otherwise: yup.string().nullable(),
            }),
            postalCode: yup.string().when('hasBusinessAddress', {
                is: true,
                then: yup.string().required('validation.required'),
                otherwise: yup.string().nullable(),
            }),
            shortDescription: yup.string().max(75, 'validation.maxLength'),
            mediumDescription: yup.string().max(500, 'validation.maxLength'),
            longDescription: yup.string().max(750, 'validation.maxLength'),
            employeesNumber: yup.string().when('employeesNumber', {
                is: val => !val?.length,
                then: yup.string().notRequired(),
                otherwise: yup.string().matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers'),
            }),
            primaryCategory: yup
                .array()
                .nullable()
                .when('secondaryCategories', {
                    is: val => val && val.length > 0,
                    then: yup.array().nullable().min(1, 'validation.required'),
                    otherwise: yup.array().nullable(),
                }),
        },
        [
            ['phoneExtension', 'phoneExtension'],
            ['employeesNumber', 'employeesNumber'],
            ['secondaryCategories', 'secondaryCategories'],
        ],
    );

    const resolver = useYupValidationResolver(CompanyFormSchema);

    const {
        getValues,
        setValue,
        control,
        handleSubmit,
        watch,
        trigger,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'onBlur',
        resolver: resolver,
        defaultValues: {
            locationName: '',
            internalName: '',
            legalName: '',
            status: '',
            brand: '',
            phoneNumber: '',
            phoneExtension: '',
            email: '',
            website: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            region: '',
            country: '',
            postalCode: '',
            hasBusinessAddress: false,
            latitude: '',
            longitude: '',
            shortDescription: '',
            mediumDescription: '',
            longDescription: '',
            openingDate: null,
            workingHoursEnabled: false,
            workingHours: [],
            logoUrl: null,
            coverUrl: null,
            chatLink: '',
            chatLinkAdded: false,
            chatType: ChatType.SMS,
            nationalHealthProviderId: '',
            employeesNumber: '',
            paymentMethods: paymentMethods.map(item => ({ name: item.name, value: false })),
            services: [{ name: '' }],
            primaryCategory: [],
            secondaryCategories: [],
            locationWebsites: [],
        },
    });

    useEffect(() => {
        if (selectedCompany?.companyId && !countriesLoading) {
            setValue('locationName', selectedCompany?.locationName || '', { shouldValidate: true });
            setValue('internalName', selectedCompany?.internalName || '', { shouldValidate: true });
            setValue('legalName', selectedCompany?.legalName || '');
            setValue('status', selectedCompany?.status || undefined, { shouldValidate: true });
            setValue('brand', selectedCompany?.brand?.id || undefined);
            setValue('phoneNumber', setInitialPhoneNumberToForm(selectedCompany?.phoneNumber, account?.locale), {
                shouldValidate: true,
            });
            setValue('phoneExtension', selectedCompany?.phoneExtension || '', { shouldValidate: true });
            setValue('email', selectedCompany?.email || '', { shouldValidate: true });
            setValue('website', selectedCompany?.website || '', { shouldValidate: true });
            setValue('addressLine1', selectedCompany?.addressLine1 || '', { shouldValidate: true });
            setValue('addressLine2', selectedCompany?.addressLine2 || '');
            setValue('city', selectedCompany?.city || '', { shouldValidate: true });
            const country = countries.find(item => item.iso2 === selectedCompany?.country);
            const isRegionReal = country?.states?.find(item => item.name === selectedCompany?.region);
            setValue('region', isRegionReal ? selectedCompany?.region : '');
            setValue('country', selectedCompany?.country || '', { shouldValidate: true });
            setValue('postalCode', selectedCompany?.postalCode || '', { shouldValidate: true });
            setValue('hasBusinessAddress', selectedCompany?.hasBusinessAddress || false);
            setValue('latitude', selectedCompany?.latitude || '');
            setValue('longitude', selectedCompany?.longitude || '');
            setValue('shortDescription', selectedCompany?.shortDescription || '', { shouldValidate: true });
            setValue('mediumDescription', selectedCompany?.mediumDescription || '', { shouldValidate: true });
            setValue('longDescription', selectedCompany?.longDescription || '', { shouldValidate: true });
            setValue('workingHours', selectedCompany?.workingHours || []);
            setValue('workingHoursEnabled', selectedCompany?.workingHoursEnabled);
            setValue('logoUrl', selectedCompany?.logoUrl);
            setValue('coverUrl', selectedCompany?.coverUrl);
            if (selectedCompany?.openingDate) {
                setValue('openingDate', new Date(parseISO(selectedCompany?.openingDate)));
            }
            setValue('chatLink', setInitialPhoneNumberToForm(selectedCompany?.chatLink, account?.locale), {
                shouldValidate: true,
            });
            setValue('chatLinkAdded', !!selectedCompany?.chatLink, { shouldValidate: true });
            setValue('chatType', selectedCompany?.chatType || ChatType.SMS);
            setValue('nationalHealthProviderId', selectedCompany?.nationalHealthProviderId || '');
            setValue('employeesNumber', selectedCompany?.employeesNumber || '', { shouldValidate: true });
            setValue(
                'paymentMethods',
                paymentMethods.map(item => ({
                    name: item.name,
                    value: selectedCompany?.paymentMethods?.includes(item.value),
                })),
            );
            setValue('services', selectedCompany?.services?.map(item => ({ name: item })) || [{ name: '' }]);
            const hasCategories = selectedCompany?.categories?.length > 0;
            const displayProp = categoriesDisplayProp[language];
            const primaryCategory = hasCategories
                ? selectedCompany?.categories
                      .filter(item => item.isPrimary)
                      .map(item => ({ id: item.id, displayName: item[displayProp] }))
                : [];
            setValue('primaryCategory', primaryCategory, { shouldValidate: true });
            const secondaryCategories = hasCategories
                ? selectedCompany?.categories
                      .filter(item => !item.isPrimary)
                      .map(item => ({ category: [{ id: item.id, displayName: item[displayProp] }] }))
                : [];
            setValue('secondaryCategories', secondaryCategories, { shouldValidate: true });
            setValue('locationWebsites', selectedCompany.locationWebsites || [], { shouldValidate: true });
        }
    }, [selectedCompany, countriesLoading]);

    const onSubmit = async () => {
        const values = getValues();
        try {
            const chatLink = preparePhoneNumberForSubmit(values.chatLink, true);
            const updatedCompany = await companyService.updateCompany({
                ...selectedCompany,
                ...values,
                brand: values.brand ? { id: values.brand } : null,
                phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber),
                openingDate: values.openingDate ? formatISO(values.openingDate, { representation: 'date' }) : null,
                chatLink: chatLink ? chatLink : null,
                chatType: values.chatLink?.length > 3 ? values.chatType : null,
                categories: [
                    { ...values.primaryCategory[0], isPrimary: true },
                    ...values.secondaryCategories
                        .filter(item => isObject(item.category[0]))
                        .map(item => ({ ...item.category[0], isPrimary: false })),
                ],
                services: values.services.map(s => s.name).filter(item => Boolean(item)),
                paymentMethods: values.paymentMethods.filter(item => item.value).map(item => PaymentTypes[item.name]),
            });
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            dispatch(accountActions.get());
            reset(values);
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        }
    };

    const onClickCancel = () => {
        dispatch(accountActions.getLocations(filter, page, rowsPerPage));
        setSelectedCompany({});
        setView(view.LIST);
    };

    return (
        <>
            <Container>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon
                        sx={{ cursor: 'pointer', fill: '#1F4C5C', marginRight: '12px' }}
                        onClick={onClickCancel}
                    />
                    <Typography
                        variant="h5"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipses',
                            whitespace: 'nowrap',
                            display: 'block',
                            color: '#1F4C5C',
                        }}
                        noWrap
                    >
                        {selectedCompany?.internalName}
                    </Typography>
                </div>
                <Content>
                    <FormProvider
                        {...{
                            control,
                            setValue,
                            getValues,
                            watch,
                            trigger,
                        }}
                    >
                        <ExpandedSection
                            isOpen={section.general}
                            onClick={() => setSection(prev => ({ ...prev, general: !prev.general }))}
                            text={t('SettingsCompanies.generalInfo')}
                        >
                            <GeneralInfo
                                selectedCompany={selectedCompany}
                                locale={account?.locale}
                                industry={account?.industry}
                            />
                        </ExpandedSection>
                        <ExpandedSection
                            isOpen={section.contact}
                            onClick={() => setSection(prev => ({ ...prev, contact: !prev.contact }))}
                            text={t('SettingsCompanies.contact')}
                        >
                            <Contacts accountLocale={account?.locale} />
                        </ExpandedSection>
                        <ExpandedSection
                            isOpen={section.address}
                            onClick={() => setSection(prev => ({ ...prev, address: !prev.address }))}
                            text={t('SettingsCompanies.address')}
                        >
                            <Address language={language} countries={countries} />
                        </ExpandedSection>
                        <ExpandedSection
                            isOpen={section.businessHours}
                            onClick={() => setSection(prev => ({ ...prev, businessHours: !prev.businessHours }))}
                            text={t('SettingsCompanies.businessHours')}
                        >
                            <BusinessHours />
                        </ExpandedSection>
                        <ExpandedSection
                            isOpen={section.additionalInfo}
                            onClick={() => setSection(prev => ({ ...prev, additionalInfo: !prev.additionalInfo }))}
                            text={t('SettingsCompanies.additionalInformation')}
                        >
                            <AdditionalInfo />
                        </ExpandedSection>
                        <ExpandedSection
                            isOpen={section.services}
                            onClick={() => setSection(prev => ({ ...prev, services: !prev.services }))}
                            text={t('SettingsCompanies.categoriesAndServices')}
                        >
                            <CategoriesAndServices />
                        </ExpandedSection>
                        <ExpandedSection
                            isOpen={section.paymentMethods}
                            onClick={() => setSection(prev => ({ ...prev, paymentMethods: !prev.paymentMethods }))}
                            text={t('common.paymentMethods')}
                        >
                            <PaymentMethods />
                        </ExpandedSection>
                        <ExpandedSection
                            isOpen={section.appearance}
                            onClick={() => setSection(prev => ({ ...prev, appearance: !prev.appearance }))}
                            text={t('common.appearance')}
                        >
                            <Appearance companyId={selectedCompany?.companyId} />
                        </ExpandedSection>
                        <IntegrationsTable
                            account={account}
                            selectedCompany={selectedCompany}
                            setSelectedCompany={setSelectedCompany}
                        />
                    </FormProvider>
                    <Profiles selectedCompany={selectedCompany} setSelectedCompany={setSelectedCompany} />
                </Content>
            </Container>
            <Footer>
                <Button
                    onClick={onClickCancel}
                    variant="outlined"
                    sx={{ '@media (max-width: 1024px)': { width: 'calc(50% - 4px)' } }}
                >
                    {t('buttons.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={isSubmitting || !isValid || !isDirty}
                    sx={{ '@media (max-width: 1024px)': { width: 'calc(50% - 4px)' } }}
                >
                    {t('buttons.saveChanges')}
                </Button>
            </Footer>
        </>
    );
};

export default EditForm;
